import { forwardRef } from 'react';

import { Flex } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { GroupBase, StylesConfig, components } from 'react-select';
import AsyncSelect, { AsyncProps } from 'react-select/async';
import { calculateDisplayEmail } from 'utils/helpers/netchexHelpers';

import { Search as SearchIcon } from 'assets/assetsV1/iconComponents';

import { Avatar } from 'uiToolkitV1/Avatar';
import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

type OptionType = {
  id: string;
  label: string;
  email?: string;
  imageUrl?: string;
};

type DeerEmployeeSearchProps = {
  loadOptions: AsyncProps<OptionType, false, GroupBase<OptionType>>['loadOptions'];
  placeholder?: string;
  components?: Partial<typeof components>;
  onChange?: (selectedOption: OptionType | null) => void;
  styles?: StylesConfig<OptionType, false, GroupBase<OptionType>>;
  onInputChange?: (inputValue: string) => void;
  defaultOptions?: boolean | OptionType[];
  menuPlacement?: 'auto' | 'bottom' | 'top';
  value?: OptionType | null;
};

const ValueContainer = ({ children, ...props }: any) =>
  components.ValueContainer && (
    <Flex width="100%" alignItems="center" padding="8px">
      <SearchIcon boxSize="16px" style={{ marginRight: '8px' }} />
      <components.ValueContainer {...props}>{children}</components.ValueContainer>
    </Flex>
  );

const Placeholder = ({ children, ...props }: any) =>
  components.Placeholder && (
    <components.Placeholder {...props}>
      <TextBodyMedium>{children}</TextBodyMedium>
    </components.Placeholder>
  );

const Option = ({ ...props }) => {
  const { data } = props ?? {};
  const { label, profilePhoto, id, companyId, companyName } = data ?? {};
  const displayEmail = calculateDisplayEmail(data);

  return (
    components.Option && (
      <components.Option {...props}>
        <Flex cursor="pointer !important" key={id} alignItems="center" height="60px" p="12px">
          <Avatar size={32} name={label} src={profilePhoto} />
          <Flex ml="12px" flexDir="column">
            <TextBodyMedium cursor="pointer">{label}</TextBodyMedium>
            <TextLabel color="text.g2" cursor="pointer">
              {companyId && companyName ? `${displayEmail} (${companyId}, ${companyName})` : displayEmail}
            </TextLabel>
          </Flex>
        </Flex>
      </components.Option>
    )
  );
};

const StyledAsyncSelect = styled(AsyncSelect<OptionType>)(({ theme }) => ({
  display: 'block',
  '.react-select-search__control': {
    border: `1px solid ${theme.colors.functional['1565BC']}`,
    minHeight: '40px',
    borderRadius: '4px',
    transition: 'width 200ms',
    '& svg': {
      color: theme.colors.neutral['999999'],
    },
    '& p': {
      color: theme.colors.neutral['999999'],
    },
    '&:hover': {
      '& svg': {
        color: theme.colors.neutral['666666'],
      },
      '& p': {
        color: theme.colors.neutral['666666'],
      },
    },
    '&--is-focused': {
      background: theme.colors.common.white,
      boxShadow: theme.shadows.search,
      border: `1px solid ${theme.colors.functional['1565BC']}`,
      '& svg': {
        color: theme.colors.text.g1,
      },
      '& p': {
        color: theme.colors.text.g1,
      },
    },
    '&--is-focused:hover': {
      background: theme.colors.common.white,
      boxShadow: theme.shadows.search,
      border: `1px solid ${theme.colors.functional['1565BC']}`,
      '& svg': {
        color: theme.colors.text.g1,
      },
      '& p': {
        color: theme.colors.text.g1,
      },
    },
    '&--menu-is-open': {
      '& + .react-select-search__menu': {},
    },
  },
  '.react-select-search__indicators': {
    display: 'none',
  },
  '.react-select-search__group ': {},
  '.react-select-search__value-container': {
    padding: '0',
  },
  '.react-select-search__input': {
    height: '16px',
  },
  '.react-select-search__input-container': {
    padding: 0,
    margin: 0,
    height: '16px',
  },
  '.react-select-search__menu': {
    backgroundColor: theme.colors.common.white,
    boxShadow: theme.shadows.large,
    border: 'none',
    padding: '8px',
  },
  '.react-select-search__option--is-focused': {
    backgroundColor: '#cae8f0',
    cursor: 'pointer',
  },
  '.react-select-search__option': {
    padding: '2px',
    backgroundColor: theme.colors.common.white,
    borderRadius: '8px',
    '&--is-selected': {
      backgroundColor: '#cae8f0',
    },
  },
  '.react-select-search__option:hover': {
    backgroundColor: '#cae8f0',
  },
  '.react-select-search__option:active': {
    // backgroundColor: theme.colors.functional.E6F1FD
  },
}));

const DeerEmployeeSearch = forwardRef<AsyncSelect<OptionType>, DeerEmployeeSearchProps>(
  (
    {
      loadOptions,
      placeholder = 'Search Mesh',
      components = {},
      onChange,
      styles,
      onInputChange,
      defaultOptions,
      menuPlacement,
      value,
    },
    ref
  ) => (
    <StyledAsyncSelect
      components={{ ...components, ValueContainer, Placeholder, Option }}
      classNamePrefix="react-select-search"
      placeholder={placeholder}
      onChange={onChange}
      loadOptions={loadOptions}
      styles={styles}
      ref={ref}
      onInputChange={onInputChange}
      defaultOptions={defaultOptions}
      menuPlacement={menuPlacement}
      value={value}
      autoFocus
    />
  )
);

export { DeerEmployeeSearch };
